/**
 * 單篇上傳「調整邊界 → 分割編輯器」動線一次加入多張時的逐張佇列(kanban 1003418)。
 *
 * 一份兩頁 PDF 會一次帶進兩個 group。原本視窗只開「最後一張」:第 1 頁沒有入口做
 * 校正與分段,送出時又只收已分段的 group(collectProcessedFilesWithRotation),
 * 第 1 頁就這樣從送件裡消失。改成佇列:從第一張開始,做完一張(確認分段或略過)
 * 或取消一張,就接著開下一張待處理的圖,直到佇列清空。
 *
 * 刻意只放純函式:setImageState 與視窗開關留在 UploadEssay 的呼叫端,
 * 這裡才測得動(專案的 vitest 是 node 環境、沒有 jsdom)。
 */
import { advanceRotationQueue } from "./rotationUi";

/**
 * 當下這張做完或被取消之後,下一張要處理哪一個 group。
 *
 * groups 要傳「這一步之後」的清單(分段完成後的、或已移除被取消那張的)。
 * 佇列裡已不在 groups 的 id 先濾掉 —— 「重設圖片」等路徑會把 group 移除,
 * 留著死 id 會讓視窗打開一個不存在的 group。前進規則與旋轉視窗的佇列相同
 * (advanceRotationQueue:移除 currentId、剩下的第一個就是下一張)。
 */
export function nextCorrectionGroupId(
  pending: readonly string[],
  groups: ReadonlyArray<{ id: string }>,
  currentId: string | null
): { pending: string[]; nextId: string | null } {
  const alive = pending.filter((id) => groups.some((group) => group.id === id));
  const next = advanceRotationQueue(alive, currentId);
  return { pending: next.pending, nextId: next.done ? null : next.currentId };
}

export interface CorrectionQueueGroup<F> {
  id: string;
  originalFile: F;
  originalUrl: string;
  processedFiles: F[];
  processedUrls: string[];
  isProcessed: boolean;
}

/**
 * 打開某一張的調整邊界視窗時,selectedImages / previewUrls 與編輯索引要長什麼樣。
 * 用在兩處:佇列換到下一張,以及分割編輯器按「上一步」回到這一張。
 *
 * 形狀刻意與「上傳第二張圖片」時 handleImageChange 產生的一致:已分段 group 的細條
 * 依序在前、這一張的原圖接在最後,編輯索引指向最後一格。調整邊界視窗與分割編輯器都靠
 * previewUrls[currentEditingImageIndex] 取圖,沿用同一個形狀就不必改它們;
 * 分段完成後 handleCropComplete 也是從 processedGroups 依序重建同一份細條清單。
 *
 * 取的是 group 自己帶的 originalUrl / originalFile,不拿 group 位置去索引舊的
 * previewUrls / originalPreviewUrls —— 分段完成後前者已被換成細條,後者在「重設圖片」
 * 後不會同步縮短,位置都對不上。
 *
 * 回傳 null = 找不到 targetId(理論上不會發生,呼叫端照原本的方式處理)。
 */
export function buildCorrectionEditor<F>(
  groups: ReadonlyArray<CorrectionQueueGroup<F>>,
  targetId: string
): { selectedImages: F[]; previewUrls: string[]; editingIndex: number } | null {
  const next = groups.find((group) => group.id === targetId);
  if (!next) return null;
  const selectedImages: F[] = [];
  const previewUrls: string[] = [];
  groups.forEach((group) => {
    if (group.isProcessed) {
      selectedImages.push(...group.processedFiles);
      previewUrls.push(...group.processedUrls);
    }
  });
  selectedImages.push(next.originalFile);
  previewUrls.push(next.originalUrl);
  return { selectedImages, previewUrls, editingIndex: previewUrls.length - 1 };
}

/**
 * 按 X 取消時要移除哪一個 group(kanban 1003418)。
 *
 * 取消的是「當下這一張」(currentProcessingGroupId),不是清單最後一張:佇列從第一張
 * 開始,第 1 張的視窗按 X 若照舊移除最後一張,被拿掉的會是還沒處理的第 2 張。
 * 找不到 currentId(理論上不會)時退回最後一張,與改動前的行為相同。
 * 一次只加一張時,當下那張本來就是最後一張,結果與改動前一致。
 */
export function resolveCancelledGroupIndex(
  groups: ReadonlyArray<{ id: string }>,
  currentId: string | null
): number {
  const index = currentId ? groups.findIndex((group) => group.id === currentId) : -1;
  return index >= 0 ? index : groups.length - 1;
}
